import Divider from '@material-ui/core/Divider';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useComplianceCalendarContext } from '@monorepo/complianceCalendar/src/components/ComplianceCalendar/ComplianceCalendarContext';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { Checkbox } from '@monorepo/shared/componentsV2/Checkbox';
import { Chip } from '@monorepo/shared/componentsV2/Chip';
import { MenuPanelHeader } from '@monorepo/shared/componentsV2/menus/MenuPanelHeader';
import { TabbableMenu } from '@monorepo/shared/componentsV2/menus/TabbableMenu';
import { bodyMedium, bodyRegular } from '@monorepo/shared/styles/text';
import Filter from '@svg/filter.svg';
import React from 'react';
import styled from 'styled-components';
import { AssigneeFilterPanel } from './AssigneeFilterPanel';
import { DEFAULT_COMPLIANCE_CALENDAR_FILTERS } from '../../consts';
import { EventTypeFilterPanel } from './EventTypeFilterPanel';

const FilterIcon = styled(Filter)`
  font-size: large;
`;

const ActiveFiltersChip = styled(Chip)`
  margin-left: 0.5rem;
`;

const StyledTabbableMenu = styled(TabbableMenu)`
  width: 22rem;
  max-height: none;
`;

const filterOptionStyle = {
  height: 'auto',
  padding: '0.75rem 1rem',
};

const RowLabel = styled.span`
  ${bodyRegular}
  color: ${({ theme }) => theme.colors.gray333};
`;

const RowValue = styled.span`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-left: auto;
  color: ${({ theme }) => theme.colors.gray666};
`;

const HideCompletedSection = styled.div`
  padding: 0.75rem 1rem;
`;

const HideCompletedLabel = styled.span`
  ${bodyMedium}
`;

export function CalendarFilters() {
  const { filters, setFilters } = useComplianceCalendarContext();

  const shouldHideCompleted = filters.hideCompleted;
  const nonDefaultEventTypeCount =
    DEFAULT_COMPLIANCE_CALENDAR_FILTERS.eventTypes.filter(
      (eventType) => !filters.eventTypes.includes(eventType),
    ).length;
  const hasEventTypeFilter = nonDefaultEventTypeCount > 0;
  const hasAssigneeFilter = filters.userIds.length > 0;

  // Number(true) resolves to 1
  const activeFilterCount =
    Number(hasEventTypeFilter) +
    Number(shouldHideCompleted) +
    Number(hasAssigneeFilter);

  return (
    <StyledTabbableMenu
      buttonElement={
        <Button color="primary" variant="outlined">
          <FilterIcon aria-hidden />
          Filters
          {activeFilterCount > 0 && (
            <ActiveFiltersChip
              color="secondary"
              label={activeFilterCount}
              variant="round"
            />
          )}
        </Button>
      }
      menuHeader={
        <MenuPanelHeader
          action={
            <Button
              color="primary"
              onClick={() => setFilters(DEFAULT_COMPLIANCE_CALENDAR_FILTERS)}
              variant="text"
            >
              Reset
            </Button>
          }
          title="Filter settings"
        />
      }
      menuFooter={
        <>
          <Divider />
          <HideCompletedSection>
            <Checkbox
              checked={shouldHideCompleted}
              label={
                <HideCompletedLabel>Hide completed events</HideCompletedLabel>
              }
              onChange={() =>
                setFilters({
                  ...filters,
                  hideCompleted: !shouldHideCompleted,
                })
              }
            />
          </HideCompletedSection>
        </>
      }
      options={[
        {
          id: 'assignee',
          style: filterOptionStyle,
          children: (
            <>
              <RowLabel>Assignee</RowLabel>
              <RowValue>
                {filters.userIds.length > 0 && (
                  <Chip
                    color="secondary"
                    label={filters.userIds.length}
                    variant="round"
                  />
                )}
                <ChevronRightIcon aria-hidden />
              </RowValue>
            </>
          ),
          nestedContent: {
            title: 'Assignee',
            headerAction: (
              <Button
                color="primary"
                onClick={() =>
                  setFilters({
                    ...filters,
                    userIds: DEFAULT_COMPLIANCE_CALENDAR_FILTERS.userIds,
                  })
                }
                variant="text"
              >
                Reset
              </Button>
            ),
            content: (
              <AssigneeFilterPanel
                onChange={(userIds) => setFilters({ ...filters, userIds })}
                selectedUserIds={filters.userIds}
              />
            ),
          },
        },
        {
          id: 'event-type',
          style: filterOptionStyle,
          children: (
            <>
              <RowLabel>Event type</RowLabel>
              <RowValue>
                {nonDefaultEventTypeCount > 0 && (
                  <Chip
                    color="secondary"
                    label={nonDefaultEventTypeCount}
                    variant="round"
                  />
                )}
                <ChevronRightIcon aria-hidden />
              </RowValue>
            </>
          ),
          nestedContent: {
            title: 'Event type',
            headerAction: (
              <Button
                color="primary"
                onClick={() =>
                  setFilters({
                    ...filters,
                    eventTypes: [
                      ...DEFAULT_COMPLIANCE_CALENDAR_FILTERS.eventTypes,
                    ],
                  })
                }
                variant="text"
              >
                Reset
              </Button>
            ),
            content: (
              <EventTypeFilterPanel
                eventTypes={filters.eventTypes}
                onChange={(eventTypes) =>
                  setFilters({ ...filters, eventTypes })
                }
              />
            ),
          },
        },
      ]}
    />
  );
}
